import React, { useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, Check, Clock3, Mail, MessageCircle, Play, RefreshCw, Sparkles, Volume2, Webhook } from 'lucide-react';
import { Link } from 'react-router';
import { playVerse } from '../services/ttsService';
import useDeliveryState from '../hooks/useDeliveryState';
import ScreenLayout from './ScreenLayout';

const dateLabel = (value) => new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
}).format(new Date(value));

const methodIcon = (method, size = 17) => (
  method === 'slack' ? <Webhook size={size} /> : <Mail size={size} />
);

export function DeliveryHistory({ deliveries, subscriptions, compact = false }) {
  const [method, setMethod] = useState('all');
  const [opened, setOpened] = useState(null);
  const subscriptionById = useMemo(
    () => Object.fromEntries(subscriptions.map((subscription) => [subscription.id, subscription])),
    [subscriptions]
  );
  const history = useMemo(() => deliveries
    .filter((delivery) => delivery.status === 'delivered' || delivery.deliveredAt || delivery.openedAt)
    .map((delivery) => {
      const subscription = subscriptionById[delivery.subscriptionId] || {};
      return {
        ...delivery,
        method: subscription.medium || 'email',
        destination: subscription.destination || '',
        when: delivery.deliveredAt || delivery.openedAt || delivery.scheduledAt
      };
    })
    .sort((left, right) => new Date(right.when) - new Date(left.when)), [deliveries, subscriptionById]);
  const methods = ['all', ...new Set(history.map((entry) => entry.method))];
  const visible = method === 'all' ? history : history.filter((entry) => entry.method === method);
  const selected = history.find((entry) => entry.id === opened);

  return (
    <section className={`vd-inbox-demo ${compact ? 'is-compact' : ''}`}>
      <header>
        <div><Clock3 size={18} /><span><strong>Delivery history</strong><small>A record of messages actually sent</small></span></div>
        {history.length ? (
          <div className="vd-inbox-toggle">
            {methods.map((value) => (
              <button className={method === value ? 'selected' : ''} onClick={() => { setMethod(value); setOpened(null); }} key={value}>
                {value === 'all' ? 'All' : value === 'email' ? 'Email' : 'Slack'}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {!history.length ? (
        <div className="vd-inbox-empty">
          <Mail size={24} />
          <strong>No deliveries yet</strong>
          <p>Messages that have actually been sent will appear here.</p>
          <Link to="/schedule">Build your first schedule</Link>
        </div>
      ) : !selected ? (
        <div className="vd-history-log">
          {visible.map((entry) => (
            <button onClick={() => setOpened(entry.id)} key={entry.id}>
              <span className={`vd-method-icon ${entry.method}`}>{methodIcon(entry.method)}</span>
              <span>
                <strong>{entry.content?.reference ? `A verse for you · ${entry.content.reference}` : 'Verse delivery'}</strong>
                <small>{entry.method === 'email' ? 'Email to' : 'Slack to'} {entry.destination}</small>
              </span>
              <span><time>{dateLabel(entry.when)}</time><em><Check size={11} />{entry.openedAt ? 'Opened' : 'Delivered'}</em></span>
            </button>
          ))}
          {!visible.length ? <div className="vd-inbox-empty"><p>No deliveries match this filter.</p></div> : null}
        </div>
      ) : (
        <article className="vd-email-open">
          <button className="vd-email-back" onClick={() => setOpened(null)}><ArrowLeft size={16} /> Delivery history</button>
          <div className="vd-delivery-method">
            <span className={`vd-method-icon ${selected.method}`}>{methodIcon(selected.method, 16)}</span>
            <p><strong>Delivered by {selected.method === 'email' ? 'email' : 'Slack webhook'}</strong><small>To {selected.destination} · {dateLabel(selected.when)}</small></p>
            <em><Check size={12} />{selected.openedAt ? 'Opened' : 'Delivered'}</em>
          </div>
          <h2>{selected.content?.reference ? `A verse for you · ${selected.content.reference}` : 'Verse delivery'}</h2>
          <p className="vd-email-from">Verse Delivery · to you</p>
          <div className="vd-email-verse">
            <div className="vd-email-landscape" />
            <blockquote>“{selected.content?.text}”</blockquote>
            <cite>{selected.content?.reference}</cite>
          </div>
          <div className="vd-email-actions">
            <button onClick={() => playVerse({ text: selected.content?.text, reference: selected.content?.reference })}><Volume2 size={16} /> Listen</button>
            <Link to="/practice/journal">Reflect on this</Link>
            <Link to="/bible/reader"><BookOpen size={16} /> Read the full chapter</Link>
          </div>
        </article>
      )}
    </section>
  );
}

export default function DeliveryPreview() {
  const { deliveries, subscriptions, loading, error, reload } = useDeliveryState();
  return (
    <ScreenLayout
      title="Delivery History"
      icon={Clock3}
      eyebrow="Your delivery record"
      className="detail-delivery-history"
      actions={<Link className="module-text-control" to="/schedule">Schedule a verse</Link>}
    >
      <div className="vd-experience">
        <header className="detail-heading">
          <p className="detail-date">Your delivery record</p>
          <h1>Your delivery history.</h1>
          <p className="detail-subtitle">Only messages that were actually sent appear here.</p>
        </header>
        {loading ? <div className="vd-state-panel" role="status">Loading your delivery history…</div> : null}
        {error ? (
          <div className="vd-state-panel" role="alert"><h2>Your history could not be loaded.</h2><p>{error}</p><button onClick={() => reload().catch(() => undefined)}><RefreshCw size={17} /> Try again</button></div>
        ) : null}
        {!loading && !error ? <DeliveryHistory deliveries={deliveries} subscriptions={subscriptions} /> : null}
        <DiscoveryRail />
      </div>
    </ScreenLayout>
  );
}

export function DiscoveryRail() {
  const communityUrl = process.env.REACT_APP_SLACK_COMMUNITY_URL;
  return (
    <section className="vd-discovery-rail">
      <Link to="/verse-studio"><span><Play size={21} /></span><div><small>Curated Scripture — never AI-written</small><strong>Verse Studio</strong><p>Open the available verse video library.</p></div></Link>
      <Link to="/prayer-lab"><span><Sparkles size={21} /></span><div><small>AI-assisted · clearly separated</small><strong>Prayer Lab</strong><p>Create a custom prayer and compare available voices.</p></div></Link>
      {communityUrl ? <a href={communityUrl} target="_blank" rel="noreferrer"><span><MessageCircle size={21} /></span><div><small>Prayer and conversation</small><strong>Join the community</strong><p>Open the configured Verse Delivery community.</p></div></a> : null}
    </section>
  );
}
